import React, { FC, useMemo } from "react";
import DashboardTile from "@components/Home/DashboardTile/DashboardTile";
import { useTranslation } from "react-i18next";
import { Box, Skeleton, Theme } from "@mui/material";
import { useGetRemindersQuery } from "@store/reminderApi";
import { useSelector } from "react-redux";
import { RootState } from "@store/store";
import ReminderItem from "@components/Home/UpcomingScheduleTile/ReminderItem";
import { CronUtil } from "@utils/cron.util";
import styled from "@emotion/styled";
import { Mode } from "@store/homeSlice";
import StyledLink from "@components/StyledLink/StyledLink";
import EmptyState from '@components/EmptyState/EmptyState';
import useMediaQuery from '@mui/material/useMediaQuery';


const Item = styled(Box, {
  shouldForwardProp: (prop) => prop !== "fullWidth",
})<{ fullWidth: boolean }>`
  ${({fullWidth}) => {
      const matches = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
      return fullWidth
              ? {width: "100%"}
              : {
                  boxSizing: "border-box",
                  flex: matches ? 1 : "1 1 290px",
                  maxWidth: matches ? "100%" : "290px",
              }
  }}
`;
const GhostLoader: FC<{ fullWidth: boolean }> = ({ fullWidth }) => {
  return (
    <Box display="flex" width="100%" gap="24px" flexWrap="wrap">
      {Array.from(Array(3)).map((_, index) => (
        <Item key={index} fullWidth={fullWidth}>
          <Skeleton variant="rectangular" width="100%" height="150px" />
        </Item>
      ))}
    </Box>
  );
};

const UpcomingScheduleTile: FC = () => {
  const { t } = useTranslation();
  const mode = useSelector((state: RootState) => state.home.mode);
  const patientId = useSelector(
    (state: RootState) => state.home.selectedPatientId,
  );

  const { data: reminders, isLoading } = useGetRemindersQuery(
    { patientId: patientId! },
    { skip: !patientId },
  );

  const sortedReminders = useMemo(() => {
    if (!reminders?.data) {
      return [];
    }
    const now = new Date();
    return reminders.data
      .filter((reminder) => reminder.isActive)
      .map((reminder) => ({
        reminder,
        nextDate: CronUtil.getNextDate(reminder.schedule,
          (reminder.startDate ? (new Date(reminder.startDate) > now ? new Date(reminder.startDate) : now) : undefined)),
      }))
      .sort((r1, r2) => r1.nextDate.getTime() - r2.nextDate.getTime())
      .splice(0, 3);
  }, [reminders]);

  return (
    <DashboardTile title={t("dashboard.upcomingSchedule.title")}>
      {!reminders?.data || isLoading ? (
        <GhostLoader fullWidth={mode === Mode.Patient} />
      ) : (
        <>
          <Box display="flex" gap="24px" flexWrap="wrap">
            {sortedReminders?.length ? (
              sortedReminders.map(({ reminder, nextDate }) => (
                <Item key={reminder.id} fullWidth={mode === Mode.Patient}>
                  <ReminderItem reminder={reminder} nextDate={nextDate} />
                </Item>
              ))
            ) : (
                <EmptyState image={`${process.env.PUBLIC_URL}/images/art-schedule2.jpeg`} label={t('dashboard.upcomingSchedule.noData')}/>
            )}
          </Box>
          {sortedReminders?.length ? (
            <div style={{ marginTop: "20px" }}>
              <StyledLink
                to={mode === Mode.Patient ? "/portal/schedule" : "/schedule"}
                //to="/schedule"
              >
                {t("common.viewAll")}
              </StyledLink>
            </div>
          ) : (
            <></>
          )}
        </>
      )}
    </DashboardTile>
  );
};

export default UpcomingScheduleTile;
